import type { TrackName, TranscriptLine } from './types'

/**
 * Fold one `transcript` message into the visible lines. Returns the new lines,
 * or null when nothing changed (so the caller can skip a re-render).
 *
 * Shared by the call itself (`CallSession`) and the panel's live console, which
 * receives the same messages relayed by the DO. It lived inside CallSession
 * until the console needed it; one copy of this rule is the point, because
 * each part of it was learned from a real call.
 *
 * THE PRODUCER SENDS A FRAGMENT ON A DELTA AND THE WHOLE UTTERANCE ON A FINAL,
 * and that is true of BOTH tracks — read off session-do.ts rather than assumed.
 * It forwards `ev.delta` for `response.output_audio_transcript.delta` and for
 * `conversation.item.input_audio_transcription.delta`, and `ev.transcript` for
 * the two matching terminal events. So a delta must APPEND and a final must
 * REPLACE, which is the same rule `applyItemTranscript` in the DO applies to
 * `turn_items`, at the same granularity: per ITEM, one line per `itemId`. This
 * used to replace on every delta, so a line only ever showed the newest
 * fragment and read as flicker until the final arrived and corrected it.
 *
 * AN EMPTY FINAL NEVER BLANKS WHAT THE DELTAS BUILT. The M0 capture contains a
 * real `...input_audio_transcription.completed` carrying `transcript: ""`;
 * taking it literally cost a whole utterance server-side once already, and that
 * is not being reintroduced on this side of the wire. An empty final is the
 * provider saying it has nothing to add, so it only marks the line finished.
 * Trimmed rather than compared against `''`, because a whitespace-only
 * authoritative text is just as empty and would take the deltas down with it.
 *
 * A DELTA THAT ARRIVES AFTER THE FINAL IS DROPPED. The final carried the whole
 * utterance, so a late fragment can only duplicate a tail that is already in
 * the line; a re-attached sideband replaying the end of a stream is the obvious
 * way to get one. Dropping it leaves the line idempotent under replay, which a
 * replayed final already is, because it replaces.
 */
export function mergeTranscriptLine(
  lines: readonly TranscriptLine[],
  itemId: string,
  who: TrackName,
  text: string,
  final: boolean,
  nowMs: number,
  maxLines: number,
): TranscriptLine[] | null {
  // Search from the end: a partial that is being extended is almost always the
  // last line, and a linear scan of six hundred items per delta would be the
  // only hot loop in the UI. The array is copied only once a line actually
  // changes, so a dropped message costs the scan and no re-render.
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i]
    if (!line || line.itemId !== itemId) continue
    if (line.final && !final) return null
    let merged: string
    if (!final) merged = line.text + text
    else if (text.trim() === '') merged = line.text
    else merged = text
    const next = lines.slice()
    next[i] = { itemId, who, text: merged, final, atMs: line.atMs }
    return next
  }
  const next = lines.slice()
  next.push({ itemId, who, text, final, atMs: nowMs })
  if (next.length > maxLines) next.splice(0, next.length - maxLines)
  return next
}
